import { attachedAgentConnectionUuidAtom } from '@/app/atoms/agentConnectionAtom';
import { atom } from 'jotai';
import { isUserPromptMessage } from 'quadratic-shared/ai/helpers/message.helper';
import { currentChatAtom } from './chatAtoms';
import {
  chatsAtom,
  contextUsageAtom,
  emptyChatSuggestionsAtom,
  importFilesToGridAtom,
  pdfImportAtom,
  promptSuggestionsAtom,
  webSearchAtom,
} from './primitives';

// ============================================================================
// Chat Count Selectors
// ============================================================================

/** Total number of saved chats */
export const chatsCountAtom = atom((get) => get(chatsAtom).length);

/** Number of messages in the current chat */
export const currentChatMessagesCountAtom = atom((get) => get(currentChatAtom).messages.length);

/** Number of user messages in the current chat */
export const currentChatUserMessagesCountAtom = atom(
  (get) => get(currentChatAtom).messages.filter((message) => isUserPromptMessage(message)).length
);

/**
 * Provider-reported context usage scoped to the current chat. Returns
 * undefined when no usage has been recorded this session (e.g. after a page
 * reload) or when the recorded usage belongs to a different chat, so the UI
 * never shows stale usage from another thread.
 */
export const currentChatContextUsageAtom = atom((get) => {
  const { usage, chatId } = get(contextUsageAtom);
  return usage && chatId && chatId === get(currentChatAtom).id ? usage : undefined;
});

// ============================================================================
// Prompt Suggestions Selectors
// ============================================================================

/** Number of prompt suggestions available */
export const promptSuggestionsCountAtom = atom((get) => get(promptSuggestionsAtom).suggestions.length);

/** Whether prompt suggestions are currently being fetched */
export const promptSuggestionsLoadingAtom = atom((get) => get(promptSuggestionsAtom).abortController !== undefined);

// ============================================================================
// Feature Loading Selectors
// ============================================================================

/** Whether PDF import is in progress */
export const pdfImportLoadingAtom = atom((get) => get(pdfImportAtom).loading);

/** Whether web search is in progress */
export const webSearchLoadingAtom = atom((get) => get(webSearchAtom).loading);

/** Whether import files to grid is in progress */
export const importFilesToGridLoadingAtom = atom((get) => get(importFilesToGridAtom).loading);

// ============================================================================
// Empty Chat Suggestions Selectors
// ============================================================================

/** Whether empty chat suggestions are currently being fetched */
export const emptyChatSuggestionsLoadingAtom = atom((get) => get(emptyChatSuggestionsAtom).loading);

// ============================================================================
// Agent Connection Selectors
// ============================================================================

/**
 * UUID of the agent connection associated with the current chat. Walks the
 * chat history backwards to find the most recent user message that pinned an
 * agent connection inline; falls back to the global "draft" attached atom
 * for the brand-new chat case where the user has selected a connection at
 * the bottom but hasn't sent a message yet. Use this anywhere the UI needs
 * to know which agent connection the active chat is centered on (e.g. the
 * schema browser's "Attached to chat" indicator).
 */
export const currentChatAgentConnectionUuidAtom = atom<string | undefined>((get) => {
  const messages = get(currentChatAtom).messages;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (isUserPromptMessage(msg) && msg.context?.agentConnection) {
      return msg.context.agentConnection.id;
    }
  }
  return get(attachedAgentConnectionUuidAtom);
});
